/**
 * TanStack query-key map for the OR-6089 `RESOURCE_UPDATED` event.
 *
 * Translates a `(resourceKind, resourceId, parentIds?)` tuple coming off
 * the socket into the concrete query keys the FE needs to invalidate so
 * the TanStack-driven detail / list pages refetch in place. Centralizing
 * the mapping here means future resource kinds add a single row instead
 * of editing the stream reducer.
 *
 * Module boundaries:
 *   - Cohort + concept-group keys live here directly — `data-access-cohorts`
 *     is a lower lib that feature-epi-chat already depends on, so the
 *     import is one-way and safe. Analysis keys come from `feature-analysis`,
 *     which does not import feature-epi-chat.
 *   - analytical variable keys are NOT imported here. The canonical `avKeys` factory lives
 *     in `feature-analytical-variables`, which itself imports types from
 *     feature-epi-chat (the analytical variable detail page renders the live `EpiArtifact`).
 *     Importing `avKeys` here would close a build-graph cycle. Instead the
 *     analytical variable resolver is INJECTED via {@link ResourceInvalidationResolvers} by
 *     the caller that mounts `useEpiChatPage` (project-page); that caller
 *     can freely import both libs because it sits above them in the graph.
 */
import type { Query, QueryKey } from '@tanstack/react-query';

import { cohortQueryKeys } from '@verantos-dev/data-access-cohorts';
import { analysisKeys } from '@verantos-dev/feature-analysis';

import { conceptGroupPreviewKeys } from './conceptGroupPreviewQueries';
import { conceptGroupStatusKeys } from './conceptGroupStatusQueries';
import type { OptimisticPatch } from './resourceEventWire';
import type { EpiResourceKind } from '../types/epi-chat-types';

export interface ResourceInvalidationContext {
  projectId: string;
  parentIds?: Record<string, string>;
  /**
   * Forwarded from the emit's ``optimisticPatch`` field so per-kind
   * keys can branch on it. Today only ``av_attached`` is used —
   * it tells ``cohortKeys`` to also invalidate the project's analytical variable
   * library list because the criteria card resolves
   * ``sourceLibraryAvId`` via that list, and a freshly-drafted analytical variable
   * isn't in the cached list yet.
   */
  optimisticPatchKind?: string;
  /** AV save signal — no usage lookup, so invalidate `/asset-staleness` for both kinds broadly. */
  refreshAssetStaleness?: boolean;
  /**
   * ``scoped_av_card_invalidation`` flag. On: an AV event invalidates only that
   * variable's card query. Off: the whole ``library`` prefix.
   */
  scopedAvInvalidation?: boolean;
}

/**
 * Per-kind key resolver. Inject analytical variable (and any future cross-feature kinds)
 * from the project-page mount; cohort / concept-group are resolved
 * inline.
 */
export type ResourceKeyResolver = (
  resourceId: string,
  context: ResourceInvalidationContext
) => QueryKey[];

export interface ResourceInvalidationResolvers {
  analyticalVariable?: ResourceKeyResolver;
  /**
   * Cohort-mutating emits that thread a ``cohortVersionId`` through
   * ``parentIds`` need to invalidate the cohort detail page's
   * criteria cache. That cache is keyed on
   * ``avKeys.list(cohortVersionId)`` in `feature-analytical-variables`,
   * which we can't import here without closing a build cycle —
   * so this resolver is injected by the project-page mount.
   *
   * Without this resolver wired, ``add_analytical_variable_to_cohort``
   * (and the other cohort-mutating tools) invalidate the cohort
   * profile cache but not the per-version criteria array, so the
   * detail page's criteria stay rendering as "Resolving to an
   * analytical variable…" placeholders until the user hard-refreshes.
   */
  cohortVersionAvList?: (cohortVersionId: string) => QueryKey[];
  /**
   * OR-6147: refetch the cohort PROFILE query so the FE learns when an agent edit
   * promoted the cohort to a server-minted NEW version. The cohort detail / builder
   * pins its definition query to ``profile.latestCohortVersionId``; that id only
   * advances when the profile query (keyed ``avKeys.profile(cohortVersionId)`` in
   * `feature-analytical-variables`) refetches and returns the new latest. A
   * cohort-mutating emit ships the PRE-upsert version id, so when the upsert mints a
   * new draft version (any edit while the cohort is not already an UNSAVED draft)
   * the version-specific ``cohortVersionAvList`` invalidation alone can't surface
   * it — the definition query stays pinned to the prior version until a manual
   * refresh (which recovers only via ``refetchOnWindowFocus``). This resolver
   * invalidates the profile-query PREFIX (version-agnostic) so the mounted profile
   * query refetches whatever version id it currently holds — robust even if that id
   * has already diverged from the emit's pre-upsert id. Injected at the project-page
   * mount for the same build-cycle reason as ``cohortVersionAvList``; no-op when
   * unwired.
   */
  cohortProfile?: () => QueryKey[];
  /**
   * The project's analytical variable library list (``useAnalyticalVariableListQuery``
   * in feature-analytical-variables, keyed at the ``avKeys.library()``
   * prefix). Invalidated when a cohort emit ships an
   * ``optimisticPatch.kind === 'av_attached'`` because the cohort
   * detail's criteria card resolves ``sourceLibraryAvId`` via the
   * library list — a freshly-drafted analytical variable that the agent attaches
   * won't appear in the cached list otherwise, and the card sticks
   * in "unresolved" state until refresh.
   */
  analyticalVariableLibrary?: () => QueryKey[];
  /**
   * Project-page caches (artifact manifest + activity feed) to refetch when
   * a cohort emit lands. Injected by the project-page mount because
   * ``feature-epi-chat`` cannot import ``feature-project-page`` without
   * closing a build cycle. Unwired on legacy mounts — no-op.
   */
  cohortProjectPageCaches?: (projectId: string) => QueryKey[];
  /**
   * Project-page caches (artifact manifest + activity feed) to refetch when
   * a concept-group emit lands. Injected by the project-page mount for the
   * same build-cycle reason as {@link cohortProjectPageCaches}. Unwired on
   * legacy mounts — no-op.
   */
  conceptGroupProjectPageCaches?: (projectId: string) => QueryKey[];
  /**
   * Project-page caches (artifact manifest + activity feed) to refetch when
   * an analysis emit lands (RA creates, edits, or processes an analysis), so
   * the project artifact list reflects it immediately. Injected by the
   * project-page mount for the same build-cycle reason as
   * {@link cohortProjectPageCaches}. Unwired on legacy mounts — no-op.
   */
  analysisProjectPageCaches?: (projectId: string) => QueryKey[];
  /**
   * OR-6461: the outer concept-group wrapper page's own detail query
   * (``conceptGroupKeys.detail(conceptGroupId)`` in
   * `feature-concept-group-detail`, which `feature-epi-chat` can't import
   * without closing a build cycle). Injected by the project-page mount, same
   * bridge rationale as {@link conceptGroupProjectPageCaches}. Unwired on
   * legacy mounts — no-op.
   */
  conceptGroupDetail?: (conceptGroupId: string) => QueryKey[];
  /**
   * Optional optimistic-patch sink. When a cohort ``RESOURCE_UPDATED``
   * arrives with an ``optimisticPatch`` field (``av_attached`` /
   * ``av_replaced`` / ``criterion_removed``) we hand the patch to this
   * callback so the FE can mutate its local cohort store BEFORE the
   * server-confirmed refetch lands. Injected at the project-page mount
   * because the chat slice cannot reach ``useCohortDefinitionStore``
   * directly without closing a build cycle. No-op when unwired — legacy
   * mounts fall back to the server-confirmed refetch.
   *
   * The patch is the wire-validated {@link OptimisticPatch} union plus the
   * ``cohortVersionId`` threaded from ``parentIds``; the resolver narrows on
   * ``patch.kind``. ``avDisplay`` (on the attach/replace kinds) carries the
   * analytical variable's library-list display fields so the resolver can
   * synthesize a library cache entry, letting ``useCriteriaSection`` resolve
   * ``sourceLibraryAvId`` immediately without waiting for the library refetch.
   */
  applyCohortOptimisticPatch?: (
    patch: OptimisticPatch & { cohortVersionId: string | undefined }
  ) => void;
  /**
   * OR-6158: fired when a cohort ``RESOURCE_UPDATED`` carries
   * ``newStatus === 'PROCESSING'`` (agent-triggered run). Flips the
   * cohort-definition store into PROCESSING so the detail header updates and
   * the 5s status poll starts — cache invalidation can't move it. Injected at
   * the project-page mount (chat slice can't reach the store without closing a
   * build cycle); no-op when unwired.
   */
  onCohortProcessingStarted?: (cohortVersionId: string | undefined) => void;
  /**
   * OR-6147: signal that an agent (chat) cohort mutation just landed, so the
   * cohort-builder store hydrates from the subsequent server refetch even when
   * it holds local staged edits (the user asked the agent to change the cohort,
   * so its server-confirmed state wins). Injected at the project-page mount
   * because the chat slice can't reach `useCohortDefinitionStore` directly.
   * No-op when unwired.
   *
   * Distinct from `applyCohortOptimisticPatch`, which only fires for the patch
   * kinds it models (e.g. `av_attached` swaps into a stub) and is a no-op for
   * mutations it doesn't (e.g. setting an index date adds a NEW criterion). This
   * flag makes the authoritative refetch win uniformly.
   */
  markCohortServerChange?: () => void;
}

const AV_LIBRARY_KEY = ['analytical-variables', 'library'] as const;

/**
 * Matches the AV library list queries (``[...avKeys.library(), searchParams, projectId]``)
 * but not per-variable detail queries, which share the prefix and carry a string ref in
 * the third slot while a list carries its search-params object.
 */
export const isAvLibraryListQuery = (query: { queryKey: QueryKey }): boolean => {
  const [namespace, scope, third] = query.queryKey;
  return (
    namespace === AV_LIBRARY_KEY[0] &&
    scope === AV_LIBRARY_KEY[1] &&
    typeof third === 'object' &&
    third !== null
  );
};

interface CachedAvDetail {
  data?: { analyticalVariableId?: string | null };
}

/**
 * Matches a library detail query that holds this variable under a different ref than
 * the event's id (a card keyed ``av.<slug>`` while the event carries the variable's UUID).
 * Queries keyed by ``resourceId`` itself are covered by the key invalidation.
 */
export const isAvDetailQueryForVariable =
  (resourceId: string) =>
  (query: Query): boolean => {
    const [namespace, scope, ref] = query.queryKey;
    if (namespace !== AV_LIBRARY_KEY[0] || scope !== AV_LIBRARY_KEY[1]) return false;
    if (typeof ref !== 'string' || ref === resourceId) return false;
    const cached = query.state.data as CachedAvDetail | undefined;
    return cached?.data?.analyticalVariableId === resourceId;
  };

/**
 * Return the set of TanStack query keys to invalidate for a resource
 * mutation. Empty arrays are valid — a kind with no resolver wired drops
 * to a no-op invalidation rather than throwing, so legacy mounts that
 * don't inject an analytical variable resolver still parse and dispatch cleanly.
 */
export const resourceInvalidationKeys = (
  resourceKind: EpiResourceKind,
  resourceId: string,
  context: ResourceInvalidationContext,
  resolvers: ResourceInvalidationResolvers = {}
): QueryKey[] => {
  switch (resourceKind) {
    case 'cohort': {
      const keys = cohortKeys(resourceId, context);
      // The cohort detail page reads its criteria array off
      // ``avKeys.list(cohortVersionId)`` (the
      // `useCohortDefinitionQuery` slot in feature-analytical-
      // variables). Without this resolver fired, a cohort emit that
      // attached / removed an analytical variable invalidates the profile but leaves
      // the criteria cache stale — the page sticks on "Resolving to
      // an analytical variable…" placeholders until refresh.
      const cohortVersionId = context.parentIds?.cohortVersionId;
      if (cohortVersionId && resolvers.cohortVersionAvList) {
        keys.push(...resolvers.cohortVersionAvList(cohortVersionId));
      }
      // Refetch the cohort profile so the FE picks up a server-minted new version
      // (the definition query re-keys to the fresh ``latestCohortVersionId``). Not
      // gated on ``cohortVersionId`` — the profile-prefix invalidation is
      // version-agnostic by design (see ``cohortProfile`` docs).
      if (resolvers.cohortProfile) {
        keys.push(...resolvers.cohortProfile());
      }
      // OR-6089: ``av_attached`` patches also need the project analytical variable
      // library list refreshed — ``useCriteriaSection`` resolves
      // ``sourceLibraryAvId`` via that list, and a freshly-drafted
      // analytical variable won't be in the cached list yet, so the criteria card
      // stays in the unresolved stub state without this push.
      if (context.optimisticPatchKind === 'av_attached' && resolvers.analyticalVariableLibrary) {
        keys.push(...resolvers.analyticalVariableLibrary());
      }
      if (resolvers.cohortProjectPageCaches) {
        keys.push(...resolvers.cohortProjectPageCaches(context.projectId));
      }
      return keys;
    }
    case 'analytical_variable': {
      // The inline AV chat card's asset-status query (``useVariableDetailByRef``)
      // is keyed ``['analytical-variables', 'library', <ref>, ...]`` (replicating the
      // avKeys.library() shape by design, see module header). Invalidate it
      // unconditionally so the card's Draft → Ready flip never depends on the host
      // injecting the resolver. Flag on, only THIS variable's entries are
      // invalidated so other mounted cards don't refetch; the library list is
      // refreshed by ``isAvLibraryListQuery``.
      // TODO: cleanup with scoped_av_card_invalidation ff
      const keys: QueryKey[] = [
        context.scopedAvInvalidation ? [...AV_LIBRARY_KEY, resourceId] : [...AV_LIBRARY_KEY],
      ];
      if (resolvers.analyticalVariable) {
        keys.push(...resolvers.analyticalVariable(resourceId, context));
      }
      // No usage lookup for which cohorts/ADs reference this AV — invalidate both kinds broadly.
      if (context.refreshAssetStaleness) {
        keys.push(
          cohortQueryKeys.assetStalenessKind('cohort'),
          cohortQueryKeys.assetStalenessKind('analytical_dataset')
        );
      }
      return keys;
    }
    case 'concept_group': {
      const keys: QueryKey[] = [];
      if (resolvers.conceptGroupProjectPageCaches) {
        keys.push(...resolvers.conceptGroupProjectPageCaches(context.projectId));
      }
      // OR-6288 Stage C: the draft tools now emit CG ``RESOURCE_UPDATED`` on
      // create/update/discard/submit, so refresh the inline card's asset-status
      // query too (keyed on the stable id) — the pill and the detail track the
      // agent's live edits. Publish (REST confirm) still relies on the
      // ``useConfirmPreview`` invalidation.
      keys.push(conceptGroupStatusKeys.detail(resourceId));
      // OR-6461: the Draft review page (Definition/Rules, Concepts, Mapping
      // tabs) is keyed on ``preview_id``, not ``concept_group_id`` — the event's
      // stable ``resourceId``. Push the preview prefix key so a prefix-match
      // invalidation covers all three ``concept-group-preview`` sub-queries in
      // one shot; without ``parentIds.previewId`` there's no way to construct
      // this key, so a mid-conversation agent edit leaves an open Draft review
      // page stale until manual reload.
      const previewId = context.parentIds?.previewId;
      if (previewId) {
        keys.push(conceptGroupPreviewKeys.prefix(previewId));
      }
      // OR-6461: the outer wrapper page's own detail query
      // (``useConceptGroupDetail`` in `feature-concept-group-detail`) is also
      // never invalidated otherwise — bridged via the injected resolver for
      // the same build-cycle reason as ``conceptGroupProjectPageCaches``.
      if (resolvers.conceptGroupDetail) {
        keys.push(...resolvers.conceptGroupDetail(resourceId));
      }
      return keys;
    }
    // The inline analysis card's status query and the detail page's profile
    // query share ``analysisKeys.profile`` — one invalidation refreshes both.
    // Analysis emits don't thread an ``analysisVersionId`` through ``parentIds``
    // (unlike cohort's ``cohortVersionId``), so there's no specific version key
    // to target — invalidate the whole ``analysisKeys.versions()`` prefix instead,
    // mirroring ``cohortProfile``'s version-agnostic invalidation. Without this,
    // ``useAnalysisVersionQuery`` (keyed on ``analysisKeys.version(versionId)``,
    // consumed by the definition tab's table-shell/cohort/variable sections)
    // never refetches when RA edits an analysis version.
    case 'analysis':
      // Readiness (the Process button's blockers) depends on the same definition.
      // TODO: cleanup with descriptive_analysis_impl ff
      return [
        analysisKeys.profile(resourceId),
        analysisKeys.versions(),
        analysisKeys.readinessAll(),
        analysisKeys.results(resourceId),
        ...(resolvers.analysisProjectPageCaches?.(context.projectId) ?? []),
      ];
  }
};

const cohortKeys = (cohortId: string, ctx: ResourceInvalidationContext): QueryKey[] => {
  const keys: QueryKey[] = [cohortQueryKeys.profile(cohortId), cohortQueryKeys.list(ctx.projectId)];
  // The analytical variable list inside a cohort version is keyed by `cohortVersionId`,
  // not `cohortId` — so an emit that adds / removes an analytical variable on a specific
  // version must thread the version id through `parentIds`. We use the
  // cohort query-key namespace's ``version`` slot to invalidate the
  // version-scoped definition cache. The analytical variable-list cache (keyed on the
  // same versionId in `feature-analytical-variables`) is reached
  // separately via the injected analytical variable resolver when the analytical variable emit fires;
  // a cohort-edit-only emit doesn't need to clobber it here.
  const cohortVersionId = ctx.parentIds?.cohortVersionId;
  if (cohortVersionId) {
    keys.push(cohortQueryKeys.version(cohortVersionId));
  }
  return keys;
};
